import React from 'react'
import { Container, Col, Row, Card } from 'react-bootstrap'
import { Dialog } from '../../components/ui/dialog/dialog'

import * as AppDefinitions from '../../App.d'
import * as Request from '../../utilities/request'
import * as I from '../../utilities/me'

import * as Helpers from './helpers'
import XeroConnect from '../../images/connect-white.svg'
import { AppContext } from '../../App'
import { XeroAccount } from '../../../../back-end/common/xeroAccount'
import { AdvancedOrgCard, OrgCard } from './orgCard'
import { XeroTrackingCategory } from '../../../../back-end/common/xeroTrackingCategories'
import { Director } from '../../constants/permission'
import { Staff } from '../../../../back-end/common/staff'
import { Navigate, useNavigate } from 'react-router-dom'
import { OnboardingStep } from '../onBoarding/onBoarding'
import * as Messages from '../../components/ui/messages/messages'

const ScreensXeroConnections = () => {
	const { appState, updateAppState } = React.useContext(AppContext)
	const [messages, updateMessages] = Messages.useMessageReducer([])
	const navigate = useNavigate()
	const [xpmConsentUrl, updateXpmConsentUrl] = React.useState<string>('')
	const [xeroConsentUrl, updateXeroConsentUrl] = React.useState<string>('')
	const [connections, updateConnections] = React.useState<Helpers.UserOrg[] | null>(null)
	const [disconnecting, updateDisconnecting] = React.useState<string[]>([])
	const [showDialog, updateShowDialog] = React.useState<string>('')
	const [xeroAccounts, updateXeroAccounts] = React.useState<XeroAccount[] | null>(null)
	const [xeroTrackingCategories, updateXeroTrackingCategories] = React.useState<XeroTrackingCategory[] | null>(null)
	const [directors, updateDirectors] = React.useState<Staff[] | null>(null)

	const getData = (authState: AppDefinitions.AuthState, permission: string | null) => {
		const getUrl = async () => {
			const [xpmReq, xeroReq] = await Promise.all([Request.get(`xpm/url/billy`), Request.get(`xero/url/billy`)])
			updateXpmConsentUrl(xpmReq.data.url)
			updateXeroConsentUrl(xeroReq.data.url)
		}

		const getConnections = async () => {
			const req = await Request.get(`organisation?where=AccountXeroOrgIsBilly==1`)
			const cons = Helpers.groupConnectionsByUser(req.data.accountXeroOrgs)
			updateConnections(cons)
		}

		const getAccounts = async () => {
			const req = await Request.get(`xeroaccount?where=XeroAccountClass==REVENUE`)
			updateXeroAccounts(req.data.xeroAccounts)
		}

		const getTrackingCategories = async () => {
			const req = await Request.get(`xerotrackingcategory?where=AccountXeroOrgIsBilly==1`)
			updateXeroTrackingCategories(req.data.xeroTrackingCategories)
		}

		const getDirectors = async () => {
			const req = await Request.get(`staff?where=BillyPermissionID==${Director}`)
			updateDirectors(req.data.staff)
		}

		if (I.have('admin', permission)) {
			getConnections()
			getUrl()
			getAccounts()
			getTrackingCategories()
			getDirectors()
		}
	}

	React.useEffect(() => {
		getData(appState.authState, appState.permission.roleID)
	}, [appState.authState, appState.permission.roleID]) // eslint-disable-line

	React.useEffect(() => {
		if (!connections) {
			return
		} // Wait until connections is available

		const xeroConnections = connections.flatMap((c) => c.orgs.filter((org) => org.apiProvider === 'xero'))
		const xpmConnections = connections.flatMap((c) => c.orgs.filter((org) => org.apiProvider === 'xpm'))

		// Onboarding: Connect XPM
		if (
			appState.onBoardingStep === OnboardingStep.ConnectXPM ||
			(localStorage.getItem('onBoardingStep') && Number(localStorage.getItem('onBoardingStep')) === OnboardingStep.ConnectXPM)
		) {
			if (xpmConnections.length > 0) {
				localStorage.setItem('onBoardingStep', '1')
				updateAppState({
					state: 'onboarding',
					data: { ...appState, onBoardingStep: OnboardingStep.ConnectXPM + 1 },
				})
				navigate('/onboarding')
			} else {
				updateMessages({
					type: 'add',
					data: {
						severity: 'info',
						message: "Welcome to Billy!\nLet's go through some onboarding steps together. Start your onboarding by connecting to a XPM.",
						timeout: 0,
						dismissible: true,
					},
				})
			}
		}

		// Onboarding: Connect Xero
		if (
			appState.onBoardingStep === OnboardingStep.ConnectXeroBlue ||
			(localStorage.getItem('onBoardingStep') && Number(localStorage.getItem('onBoardingStep')) === OnboardingStep.ConnectXeroBlue)
		) {
			if (xeroConnections.length > 0) {
				localStorage.removeItem('onBoardingStep')
				updateAppState({
					state: 'onboarding',
					data: { ...appState, onBoardingStep: OnboardingStep.ConnectXeroBlue + 1 },
				})
			} else {
				updateMessages({
					type: 'add',
					data: {
						severity: 'info',
						message: 'Now lets connect to any Xero files which you wish to issue invoices from.',
						timeout: 0,
						dismissible: true,
					},
				})
			}
		}
	}, [appState.onBoardingStep, connections])

	const disconnectOrg = async (connectionID: string) => {
		await Request.del(`xpm/tokens/billy/${connectionID}`)
		updateDisconnecting(disconnecting.filter((d) => d !== connectionID))
		getData(appState.authState, appState.permission.roleID)
	}

	if (!I.have('admin', appState.permission.roleID)) {
		return (
			<Container className="oversight-container">
				<Row>
					<Col></Col>
					<Col xs="auto">
						<p>You do not have permission to view this page.</p>
					</Col>
					<Col></Col>
				</Row>
			</Container>
		)
	}

	if (xeroConsentUrl === '' || xpmConsentUrl === '' || connections === null) {
		//loading
		return <div></div>
	}

	const xeroConnections = connections.flatMap((c) => c.orgs.filter((org) => org.apiProvider === 'xero'))
	const xpmConnections = connections.flatMap((c) => c.orgs.filter((org) => org.apiProvider === 'xpm'))

	// check if we are in onBoarding
	if (appState.onBoardingStep === OnboardingStep.ConnectXeroBlue + 1) {
		return <Navigate to="/onboarding" />
	}

	return (
		<Container className="oversight-container">
			<Row className="mb-3">
				<Col sm="auto">
					<h2>XPM Connection</h2>
				</Col>
				<Col />
				<Col sm="auto">
					<a href={xpmConsentUrl} style={{ color: '' }}>
						<img src={XeroConnect} alt="Connect to XPM" />
					</a>
				</Col>
			</Row>
			<Row>
				<Col>
					{xpmConnections.length > 0 ? (
						xpmConnections.map((con) => (
							<Card key={con.xeroOrgID} className="p-3">
								<OrgCard
									org={con}
									consentUrl={xpmConsentUrl}
									disconnecting={disconnecting.includes(con.connectionID)}
									updateShowDialog={updateShowDialog}
									accounts={[]}
									trackingCategories={[]}
									directors={[]}
									handleUpdateAccount={() => {}}
								/>
							</Card>
						))
					) : (
						<Row className="p-3">No XPM Connection</Row>
					)}
				</Col>
			</Row>
			<Row className="mb-3 mt-3">
				<Col sm="auto">
					<h2>Xero Connections</h2>
				</Col>
				<Col></Col>
				<Col sm="auto">
					<a href={xeroConsentUrl} style={{ color: 'inherit' }}>
						<img src={XeroConnect} alt="Connect new Xero" />
					</a>
				</Col>
			</Row>
			<Row>
				<Col>
					{xeroConnections.length > 0 ? (
						xeroConnections.map((con) => (
							<AdvancedOrgCard
								key={con.xeroOrgID}
								org={con}
								consentUrl={xpmConsentUrl}
								disconnecting={disconnecting.includes(con.connectionID)}
								updateShowDialog={updateShowDialog}
								accounts={xeroAccounts?.filter((xa) => xa.xeroOrgID === con.xeroOrgID) || []}
								trackingCategories={xeroTrackingCategories?.filter((xtc) => xtc.xeroOrgID === con.xeroOrgID) || []}
								directors={directors || []}
								handleUpdateAccount={() => {}}
							/>
						))
					) : (
						<Card className="p-3">No Xero Connections</Card>
					)}
				</Col>
			</Row>
			{showDialog ? (
				<Dialog
					show={true}
					handleClose={() => updateShowDialog('')}
					handleConfirm={() => {
						updateDisconnecting([...disconnecting, showDialog])
						disconnectOrg(showDialog)
					}}
					titleText={'Disconnect Xero Organisation'}
					bodyText={'Are you sure you want to disconnect this organisation?'}
				/>
			) : null}
			<Messages.Messages messages={messages} updateMessage={updateMessages} />
		</Container>
	)
}

export default ScreensXeroConnections
